import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  IconButton, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  useTheme,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close, DeleteOutline, Receipt } from '@mui/icons-material';
import { LoyaltyProgram } from '../../types';

interface CreditCardDisplayProps {
  program: LoyaltyProgram;
  cardType?: 'platinum' | 'gold' | 'premium';
  children?: React.ReactNode;
  onDelete?: (program: LoyaltyProgram) => void;
  showDeleteButton?: boolean;
  className?: string;
  transactions?: any[]; // Add transactions prop
  onViewTransactions?: () => void; // Add callback for viewing transactions
}

// Credit card background images based on card type and network
const getCardBackground = (cardType: string, cardNetwork?: string) => {
  const networkDesigns = {
    visa: {
      background: 'linear-gradient(135deg, #1a1f71 0%, #0066b2 50%, #1a1f71 100%)',
      pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    mastercard: {
      background: 'linear-gradient(135deg, #eb001b 0%, #f79e1b 50%, #eb001b 100%)',
      pattern: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.06'%3E%3Ccircle cx='11' cy='11' r='11'/%3E%3Ccircle cx='49' cy='11' r='11'/%3E%3Ccircle cx='11' cy='49' r='11'/%3E%3Ccircle cx='49' cy='49' r='11'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    amex: {
      background: 'linear-gradient(135deg, #006fcf 0%, #00519e 50%, #006fcf 100%)',
      pattern: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.04'%3E%3Cpath d='M20 20.5V18H0v-2h20v-2H0v-2h20v-2H0V8h20V6H0V4h20V2H0V0h22v20h2V0h2v20h2V0h2v20h2V0h2v20h2V0h2v20.5L40 20v2L20 22z'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    discover: {
      background: 'linear-gradient(135deg, #ff6000 0%, #ff4500 50%, #ff6000 100%)',
      pattern: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.03'%3E%3Cpath d='M0 0h80v80H0V0zm20 20v40h40V20H20z'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    default: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
      pattern: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='0.05'%3E%3Cpath d='M0 0h20v20H0V0zm10 18a8 8 0 100-16 8 8 0 000 16z'/%3E%3C/g%3E%3C/svg%3E")`,
    }
  };

  const design = networkDesigns[cardNetwork as keyof typeof networkDesigns] || networkDesigns.default;
  
  // Add tier-specific metallic effects
  const tierEffects = {
    platinum: 'linear-gradient(135deg, rgba(192, 192, 192, 0.4) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(192, 192, 192, 0.4) 100%)',
    gold: 'linear-gradient(135deg, rgba(255, 215, 0, 0.4) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 215, 0, 0.4) 100%)',
    basic: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)',
    premium: 'linear-gradient(135deg, rgba(138, 43, 226, 0.2) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(138, 43, 226, 0.2) 100%)',
  };

  const tierEffect = tierEffects[cardType as keyof typeof tierEffects] || tierEffects.basic;
  
  return {
    background: `${design.background}, ${tierEffect}`,
    backgroundImage: design.pattern,
  };
};

const CreditCardContainer = styled(Card)<{ cardtype: string; programcolor: string; cardnetwork?: string }>(({ theme, cardtype, programcolor, cardnetwork }) => {
  const cardDesign = getCardBackground(cardtype, cardnetwork);
  
  return {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    aspectRatio: '1.586', // Standard credit card ratio
    background: cardDesign.background,
    backgroundImage: cardDesign.backgroundImage,
    backgroundSize: 'auto',
    backgroundRepeat: 'repeat',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minHeight: '200px',
    // Better shadows and contrast for light theme
    boxShadow: theme.palette.mode === 'light' 
      ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)' 
      : '0 4px 20px rgba(0, 0, 0, 0.3)',
    
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'light'
        ? `0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 12px ${programcolor}30`
        : `0 12px 40px -4px ${programcolor}40`,
      '& .delete-button': {
        opacity: 1,
      },
    },
    
    // Enhanced background overlay for better readability
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette.mode === 'light' 
        ? 'linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.65) 100%)'
        : 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
      zIndex: 1,
    },
  
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '20%',
      right: '-20%',
      width: '150%',
      height: '150%',
      background: theme.palette.mode === 'light'
        ? 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 50%)'
        : 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
      zIndex: 1,
    }
  };
});

const CardNetwork = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '50px', // Move left to avoid delete button
  zIndex: 6, // Higher than content overlay
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '4px',
  padding: '2px 6px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  '& img': {
    width: '32px',
    height: 'auto',
  }
}));

const CardChip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50px',
  left: '20px',
  width: '32px',
  height: '26px',
  background: 'linear-gradient(135deg, #ffd700 0%, #ffed4a 100%)',
  borderRadius: '6px',
  zIndex: 3,
  border: '1px solid rgba(0,0,0,0.15)',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));

const CardNumber = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  bottom: '45px',
  left: '20px',
  right: '20px',
  zIndex: 3,
  fontFamily: 'Courier New, monospace',
  fontSize: '15px',
  fontWeight: 600,
  letterSpacing: '1.5px',
  textShadow: theme.palette.mode === 'light'
    ? '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)'
    : '0 2px 4px rgba(0,0,0,0.5)',
  color: 'white',
}));

const CardHolder = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  bottom: '15px',
  left: '20px',
  right: '60px', // Leave space for delete button
  zIndex: 3,
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  textShadow: theme.palette.mode === 'light'
    ? '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)'
    : '0 2px 4px rgba(0,0,0,0.5)',
  color: 'white',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));



// Credit card network images/icons
const getCardNetworkIcon = (cardType?: string) => {
  switch (cardType) {
    case 'visa':
      return { text: 'VISA', color: '#1a1f71', bgColor: 'white' };
    case 'mastercard':
      return { text: 'MC', color: '#eb001b', bgColor: 'white' };
    case 'amex':
      return { text: 'AMEX', color: '#006fcf', bgColor: 'white' };
    case 'discover':
      return { text: 'DISCOVER', color: '#ff6000', bgColor: 'white' };
    default:
      return { text: 'CARD', color: '#666', bgColor: 'white' };
  }
};

const formatCardNumber = (lastFour?: string) => {
  if (!lastFour) return '•••• •••• •••• ••••';
  return `•••• •••• •••• ${lastFour}`;
};

const CreditCardDisplay: React.FC<CreditCardDisplayProps> = ({ 
  program, 
  cardType = 'premium',
  children,
  onDelete,
  showDeleteButton = true,
  className,
  transactions = [],
  onViewTransactions,
}) => {
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const cardNetworkIcon = getCardNetworkIcon(program.cardType);
  const maskedNumber = formatCardNumber(program.cardNumber);

  const transactionCount = transactions.filter(t => t.programId === program.id).length;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(program);
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleViewTransactions = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewTransactions) {
      onViewTransactions();
    }
  };

  return (
    <>
      <Box>
        <CreditCardContainer 
          cardtype={cardType} 
          programcolor={program.color}
          cardnetwork={program.cardType}
          elevation={8}
          className={`CreditCardContainer ${className || ''}`}
        >
        <CardContent sx={{ 
          position: 'relative', 
          zIndex: 2, 
          height: '100%',
          padding: 0,
          '&:last-child': { paddingBottom: 0 }
        }}>
          {/* Delete Button */}
          {showDeleteButton && onDelete && (
            <IconButton
              className="delete-button"
              onClick={handleDeleteClick}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 6,
                width: 32,
                height: 32,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                opacity: 0,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}

          {/* Card Network Logo */}
          <CardNetwork>
            <Box sx={{
              background: cardNetworkIcon.bgColor,
              color: cardNetworkIcon.color,
              px: 1,
              py: 0.5,
              borderRadius: '4px',
              minWidth: '45px',
              textAlign: 'center',
            }}>
              <Typography variant="caption" sx={{ 
                fontWeight: 800, 
                fontSize: '8px',
              }}>
                {cardNetworkIcon.text}
              </Typography>
            </Box>
          </CardNetwork>

          {/* EMV Chip */}
          <CardChip />

          {/* Card Number */}
          <CardNumber>
            {maskedNumber}
          </CardNumber>

          {/* Cardholder Name */}
          <CardHolder>
            {program.name}
          </CardHolder>

          {/* Top overlay for points/stats - positioned to not interfere with card elements */}
          {children && (
            <Box sx={{ 
              position: 'absolute', 
              top: '40%', // Positioned in vertical middle
              left: 12, 
              right: 12,
              zIndex: 5,
              color: 'white',
              textShadow: theme.palette.mode === 'light'
                ? '0 2px 8px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)'
                : '0 2px 4px rgba(0,0,0,0.5)',
            }}>
              {children}
            </Box>
          )}
        </CardContent>
      </CreditCardContainer>

        {/* View Transactions Button */}
        {onViewTransactions && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Receipt />}
            onClick={handleViewTransactions}
            sx={{
              mt: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(66, 153, 225, 0.1)' : 'rgba(26, 54, 93, 0.05)',
              },
            }}
          >
            View Transactions
            {transactionCount > 0 && (
              <Chip
                label={transactionCount}
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: 'primary.main',
                  color: 'white',
                }}
              />
            )}
          </Button>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          pb: 2 
        }}>
          <DeleteOutline color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Remove Credit Card
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. All transaction history and rewards data for this card will be removed.
            </Typography>
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{program.name}</strong> ending in <strong>••••{program.cardNumber}</strong>?
          </Typography>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: 'background.default', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Card Details:
            </Typography>
            <Typography variant="body2">
              • <strong>Points/Miles:</strong> {program.points.toLocaleString()} {program.currency}
            </Typography>
            <Typography variant="body2">
              • <strong>Tier:</strong> {program.tier}
            </Typography>
            <Typography variant="body2">
              • <strong>Network:</strong> {program.cardType?.toUpperCase()}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Keep Card
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
            startIcon={<DeleteOutline />}
          >
            Remove Card
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreditCardDisplay;